import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import Panel from '../../../controls/Panel';
import Typography from '@material-ui/core/Typography';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import MaterialTable, { MTableBodyRow } from 'material-table';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '../../../controls/Button';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import AddIcon from '@material-ui/icons/Add';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import CopyIcon from '@material-ui/icons/FileCopy';
import CloseIcon from '@material-ui/icons/Close';
import { withSnackbar } from 'notistack';

const styles = (theme) => ({
    table: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    center: {
        textAlign: 'center',
    },
    container: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    title: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    addButton: {
        marginLeft: theme.spacing(1),
        borderColor: theme.palette.divider,
        padding: theme.spacing(1),
    },
    tableRow: {
        '&:hover $formAction': {
            visibility: 'visible',
        },
    },
    formAction: {
        visibility: 'hidden',
    },
});

class FormsPanel extends React.Component {
    state = {
        deleteDialog: false,
        copyDialog: false,
        form: null,
    };

    startFormNamer = () => {
        this.props.history.push('/formName');
    };

    handleOpenSnackbar = (variant) => {
        const message = variant === 'success' ? 'Saved successfully!' : 'Your form could not be saved';
        this.props.enqueueSnackbar(message, {
            variant: variant,
            action: (key) => (
                <IconButton
                    key={key}
                    aria-label={key}
                    color="inherit"
                    size="small"
                    onClick={() => this.props.closeSnackbar(key)}
                >
                    <CloseIcon />
                </IconButton>
            ),
        });
    };

    selectForm = (form) => {
        this.props.history.push(`/form/${form.id}`);
    };

    handlePopupDelete = (event, form) => {
        event.stopPropagation();
        this.setState({
            deleteDialog: true,
            form: form,
        });
    };

    handlePopupCopy = (event, form) => {
        event.stopPropagation();
        this.setState({
            copyDialog: true,
            form: form,
        });
    };

    handleDelete = () => {
        this.props.onDelete(this.state.form);
        this.setState({
            deleteDialog: false,
        });
    };

    handleCopy = () => {
        this.props.onStartNew(this.state.form.name + '-copy', this.state.form, (variant) => {
            if (variant === 'success') {
                this.props.history.push(`/form/${this.props.form.id}`);
            }
            this.handleOpenSnackbar(variant);
        });

        this.setState({
            copyDialog: false,
        });
    };

    handleCancelDelete = () => {
        this.setState({
            deleteDialog: false,
        });
    };

    handleCancelCopy = () => {
        this.setState({
            copyDialog: false,
        });
    };

    render() {
        const deleteDialog = (
            <Dialog open={this.state.deleteDialog}>
                <DialogTitle>Wait!</DialogTitle>
                <DialogContent>
                    <DialogContentText id="deleteForm">
                        Are you sure you want to permanently delete this form?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={this.handleDelete} variant="contained" color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        );
        const copyDialog = (
            <Dialog open={this.state.copyDialog}>
                <DialogTitle>Wait!</DialogTitle>
                <DialogContent>
                    <DialogContentText id="copyForm">Are you sure you want to copy this form?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleCancelCopy} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={this.handleCopy} variant="contained" color="primary">
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>
        );

        return (
            <React.Fragment>
                <Panel>
                    <div className={this.props.classes.container}>
                        <div className={this.props.classes.title}>
                            <Typography variant="h6">Your Forms</Typography>
                        </div>

                        <div className={this.props.classes.addButton}>
                            <Tooltip title="Create New Form">
                                <Button variant="contained" color="green" onClick={this.startFormNamer}>
                                    <AddIcon />
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    <Hidden xsDown>
                        <MaterialTable
                            options={{
                                toolbar: false,
                                paging: false,
                                draggable: false,
                            }}
                            components={{
                                Container: ({ children }) => <div className={this.props.classes.table}>{children}</div>,
                                Row: (props) => <MTableBodyRow className={this.props.classes.tableRow} {...props} />,
                            }}
                            localization={{
                                body: {
                                    emptyDataSourceMessage: 'No Forms To Display',
                                },
                            }}
                            columns={[
                                { title: 'Form ID', field: 'id' },
                                { title: 'Form Name', field: 'name' },
                                {
                                    render: (rowData) => (
                                        <Tooltip className={this.props.classes.formAction} title="Copy">
                                            <IconButton
                                                aria-label="copy"
                                                onClick={(event) => this.handlePopupCopy(event, rowData)}
                                            >
                                                <CopyIcon color="action" />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                    cellStyle: {
                                        textAlign: 'right',
                                        padding: 0,
                                    },
                                    headerStyle: {
                                        visibility: 'hidden',
                                    },
                                    width: '48px',
                                    sorting: false,
                                },
                                {
                                    render: (rowData) => (
                                        <Tooltip className={this.props.classes.formAction} title="Delete">
                                            <IconButton
                                                aria-label="delete"
                                                onClick={(event) => this.handlePopupDelete(event, rowData)}
                                            >
                                                <DeleteIcon color="action" />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                    cellStyle: {
                                        textAlign: 'right',
                                        padding: 0,
                                    },
                                    headerStyle: {
                                        visibility: 'hidden',
                                    },
                                    width: '48px',
                                    sorting: false,
                                },
                            ]}
                            data={this.props.forms}
                            onRowClick={(evt, row) => this.selectForm(row)}
                        />
                    </Hidden>
                    <Hidden smUp>
                        {this.props.forms && (
                            <List>
                                {this.props.forms.map((form) => (
                                    <ListItem key={form.id} button onClick={() => this.selectForm(form)}>
                                        <ListItemText primary={form.name} secondary={form.id} />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Copy">
                                                <IconButton
                                                    aria-label="copy"
                                                    onClick={(event) => this.handlePopupCopy(event, form)}
                                                >
                                                    <CopyIcon color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    aria-label="delete"
                                                    onClick={(event) => this.handlePopupDelete(event, form)}
                                                >
                                                    <DeleteIcon color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Hidden>
                </Panel>
                {deleteDialog}
                {copyDialog}
            </React.Fragment>
        );
    }
}

export default withRouter(withStyles(styles)(withSnackbar(FormsPanel)));
